import React, { useState, useEffect } from 'react';
import { ChevronRight, UserPlus } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import SeatsTable from './SeatsTable';
import AddMemberPanel from './AddMemberPanel';
import AvatarUploadSection from './AvatarUploadSection';
import OrganizationBasicInfo from './OrganizationBasicInfo';
import { useTheme } from '../../ThemeContext';


export default function OrganizationSettingsView({ 
  selectedOrganization,
  walletAddress,
  setView,
  onOrganizationUpdated,
  onOrganizationDeleted
}) {
  const { t, isDark } = useTheme();
  // Form state
  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [originalHandle, setOriginalHandle] = useState('');
  const [handleAvailable, setHandleAvailable] = useState(null);
  const [checkingHandle, setCheckingHandle] = useState(false);
  
  // UI state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Avatar state
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarError, setAvatarError] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Seats state
  const [seats, setSeats] = useState([]);
  const [seatsWithNames, setSeatsWithNames] = useState([]);
  const [loadingSeats, setLoadingSeats] = useState(true);
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [addingMember, setAddingMember] = useState(false);

  // Invite state
  const [invites, setInvites] = useState([]);
  const [copiedInvite, setCopiedInvite] = useState(null);

  // Load organization data
  useEffect(() => {
    if (selectedOrganization) {
      setName(selectedOrganization.name || '');
      setEmail(selectedOrganization.email || '');
      setWebsite(selectedOrganization.website || '');
      setLocation(selectedOrganization.location || '');
      setBio(selectedOrganization.bio || '');
      
      loadHandle();
      loadSeats();
      loadInvites();
    }
  }, [selectedOrganization]);

  const loadHandle = async () => {
    if (!selectedOrganization?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('entity_handles')
        .select('handle')
        .eq('entity_type', 'organization')
        .eq('entity_id', selectedOrganization.id)
        .single();
      
      if (!error && data) {
        setHandle(data.handle);
        setOriginalHandle(data.handle);
      }
    } catch (err) {
      console.error('Error loading handle:', err);
    }
  };

  const loadSeats = async () => {
    if (!selectedOrganization?.id) return;
    
    setLoadingSeats(true);
    try {
      const { data, error } = await supabase
        .from('org_seats')
        .select('*')
        .eq('org_id', selectedOrganization.id)
        .order('created_at', { ascending: true });
      
      if (!error && data) {
        setSeats(data);
        
        const seatsWithNameData = await Promise.all(
          data.map(async (seat) => {
            const { data: userData } = await supabase
              .from('users')
              .select('display_name')
              .eq('wallet_address', seat.wallet_address)
              .single();
            
            return {
              ...seat,
              display_name: userData?.display_name || 'Member'
            };
          })
        );
        
        setSeatsWithNames(seatsWithNameData);
      }
    } catch (err) {
      console.error('Error loading seats:', err);
    }
    setLoadingSeats(false);
  };

  const loadInvites = async () => {
    if (!selectedOrganization?.id) return;
    
    try {
      const now = Date.now();
      const { data, error } = await supabase
        .from('org_invites')
        .select('*')
        .eq('org_id', selectedOrganization.id)
        .gt('expires_at', now)
        .order('created_at', { ascending: false });
      
      if (!error && data) {
        setInvites(data);
      }
    } catch (err) {
      console.error('Error loading invites:', err);
    }
  };

  // Check handle availability
  useEffect(() => {
    if (!handle.trim() || handle === originalHandle) {
      setHandleAvailable(null);
      return;
    }

    const checkHandle = async () => {
      setCheckingHandle(true);
      try {
        const { data, error } = await supabase
          .from('entity_handles')
          .select('handle')
          .eq('handle', handle.toLowerCase())
          .single();

        if (error && error.code === 'PGRST116') {
          setHandleAvailable(true);
        } else if (data) {
          setHandleAvailable(false);
        }
      } catch (err) {
        console.error('Error checking handle:', err);
      }
      setCheckingHandle(false);
    };

    const debounce = setTimeout(checkHandle, 500);
    return () => clearTimeout(debounce);
  }, [handle, originalHandle]);

  // Avatar handlers
  const handleAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setAvatarError('');

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setAvatarError('Please upload a PNG or JPG image');
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setAvatarError('Image must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
      setAvatarFile(file);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = async () => {
    if (avatarPreview && !selectedOrganization.avatar_url) {
      setAvatarFile(null);
      setAvatarPreview(null);
      setAvatarError('');
      return;
    }

    if (selectedOrganization.avatar_url) {
      const confirmed = window.confirm('Remove organization logo?');
      if (!confirmed) return;

      setIsSubmitting(true);
      try {
        if (selectedOrganization.avatar_url.includes('org-avatars/')) {
          const urlParts = selectedOrganization.avatar_url.split('org-avatars/');
          const fileName = urlParts[1];
          
          await supabase.storage
            .from('org-avatars')
            .remove([`avatars/${fileName}`]);
        }

        const { error } = await supabase
          .from('organizations')
          .update({ avatar_url: null })
          .eq('id', selectedOrganization.id);

        if (error) throw error;

        setAvatarFile(null);
        setAvatarPreview(null);
        setAvatarError('');
        
        if (onOrganizationUpdated) {
          await onOrganizationUpdated();
        }

        setSuccess('Logo removed');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        console.error('Error removing avatar:', err);
        setError('Failed to remove logo');
      }
      setIsSubmitting(false);
    } else {
      setAvatarFile(null);
      setAvatarPreview(null);
      setAvatarError('');
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return null;

    setUploadingAvatar(true);
    try {
      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `${selectedOrganization.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { data, error: uploadError } = await supabase.storage
        .from('org-avatars')
        .upload(filePath, avatarFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      const { data: urlData } = supabase.storage
        .from('org-avatars')
        .getPublicUrl(filePath);

      setUploadingAvatar(false);
      return urlData.publicUrl;
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setAvatarError(`Failed to upload image: ${err.message}`);
      setUploadingAvatar(false);
      return null;
    }
  };

  // Seat management handlers
  const handleAddByWallet = async (newWalletAddress, newMemberRole) => {
    if (!newWalletAddress.trim()) {
      setError('Please enter a wallet address');
      return;
    }

    const existingSeat = seats.find(s => s.wallet_address === newWalletAddress.trim());
    if (existingSeat) {
      setError('This wallet already has a seat in this organization');
      return;
    }

    setAddingMember(true);
    setError('');

    try {
      const seatId = `seat_${Date.now()}`;
      const { error } = await supabase
        .from('org_seats')
        .insert({
          id: seatId,
          org_id: selectedOrganization.id,
          wallet_address: newWalletAddress.trim(),
          role: newMemberRole,
          created_at: Date.now()
        });

      if (error) throw error;

      setSuccess('Member added successfully!');
      setShowAddMethod(false);
      await loadSeats();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error adding member:', err);
      setError('Failed to add member. Please try again.');
    }
    setAddingMember(false);
  };

  const handleGenerateInvite = async (inviteRole, inviteMaxUses, inviteExpireDays) => {
    setError('');

    try {
      const inviteId = `invite_${Date.now()}`;
      const inviteCode = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      const expiresAt = Date.now() + (inviteExpireDays * 24 * 60 * 60 * 1000);

      const { error } = await supabase
        .from('org_invites')
        .insert({
          id: inviteId,
          org_id: selectedOrganization.id,
          role: inviteRole,
          invite_code: inviteCode,
          max_uses: inviteMaxUses,
          uses_count: 0,
          expires_at: expiresAt,
          created_by: walletAddress,
          created_at: Date.now()
        });

      if (error) throw error;

      setSuccess('Invite link generated!');
      setShowAddMethod(false);
      await loadInvites();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error generating invite:', err);
      setError('Failed to generate invite link. Please try again.');
    }
  };

  const handleRoleChange = async (seatId, newRole) => {
    try {
      const { error } = await supabase
        .from('org_seats')
        .update({ role: newRole })
        .eq('id', seatId);

      if (error) throw error;

      setSuccess('Role updated');
      await loadSeats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating role:', err);
      setError('Failed to update role');
    }
  };

  const handleRemoveSeat = async (seatId, role) => {
    if (role === 'primary_owner') {
      setError('Cannot remove primary owner');
      return;
    }

    const confirmed = window.confirm('Remove this member from the organization?');
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from('org_seats')
        .delete()
        .eq('id', seatId);

      if (error) throw error;

      setSuccess('Member removed');
      await loadSeats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error removing seat:', err);
      setError('Failed to remove member');
    }
  };

  const copyInviteLink = (inviteCode) => {
    const inviteUrl = `${window.location.origin}/invite/${inviteCode}`;
    navigator.clipboard.writeText(inviteUrl);
    setCopiedInvite(inviteCode);
    setTimeout(() => setCopiedInvite(null), 2000);
  };

  const revokeInvite = async (inviteId) => {
    const confirmed = window.confirm('Revoke this invite link?');
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from('org_invites')
        .delete()
        .eq('id', inviteId);

      if (error) throw error;

      setSuccess('Invite revoked');
      await loadInvites();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error revoking invite:', err);
      setError('Failed to revoke invite');
    }
  };

  // Form submission
  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!handle.trim()) {
      setError('Handle is required');
      return;
    }
    if (handle !== originalHandle && handleAvailable === false) {
      setError('Handle is already taken');
      return;
    }
    if (bio.length > 200) {
      setError('Bio must be 200 characters or less');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      let avatarUrl = selectedOrganization.avatar_url;
      if (avatarFile) {
        const uploadedUrl = await uploadAvatar();
        if (uploadedUrl) {
          avatarUrl = uploadedUrl;
        } else {
          setIsSubmitting(false);
          return;
        }
      }

      const { error: orgError } = await supabase
        .from('organizations')
        .update({
          name: name.trim(),
          bio: bio.trim() || null,
          email: email.trim() || null,
          website: website.trim() || null,
          location: location.trim() || null,
          avatar_url: avatarUrl
        })
        .eq('id', selectedOrganization.id);

      if (orgError) throw orgError;

      if (handle !== originalHandle) {
        const { error: handleError } = await supabase
          .from('entity_handles')
          .update({
            handle: handle.toLowerCase()
          })
          .eq('entity_type', 'organization')
          .eq('entity_id', selectedOrganization.id);

        if (handleError) throw handleError;
        setOriginalHandle(handle);
      }

      setSuccess('Settings saved successfully!');
      
      setAvatarFile(null);
      setAvatarPreview(null);
      
      if (selectedOrganization?.id) {
        const { data: updatedOrg } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', selectedOrganization.id)
          .single();
        
        if (updatedOrg && onOrganizationUpdated) {
          await onOrganizationUpdated();
        }
      }
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating organization:', err);
      setError('Failed to save settings. Please try again.');
    }
    
    setIsSubmitting(false);
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${selectedOrganization.name}"? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;

    setIsSubmitting(true);
    setError('');

    try {
      const { error: handleError } = await supabase
        .from('entity_handles')
        .delete()
        .eq('entity_type', 'organization')
        .eq('entity_id', selectedOrganization.id);

      if (handleError) throw handleError;

      const { error: orgError } = await supabase
        .from('organizations')
        .delete()
        .eq('id', selectedOrganization.id);

      if (orgError) throw orgError;

      if (onOrganizationDeleted) {
        onOrganizationDeleted();
      }
      setView('profile');
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError('Failed to delete organization. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!selectedOrganization) return null;

  const userRole = selectedOrganization.userRole;
  const canManageSeats = ['primary_owner', 'owner', 'admin'].includes(userRole);
  const canDelete = userRole === 'primary_owner';

  return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      {/* Back Button */}
      <button
        onClick={() => setView('profile')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          color: t.text,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '16px',
          fontFamily: 'Poppins, sans-serif',
          fontWeight: '500',
          padding: '0',
          marginBottom: '40px'
        }}
      >
        <ChevronRight size={20} style={{ transform: 'rotate(180deg)' }} />
        Return to Wallet
      </button>

      {/* Settings Form */}
      <div style={{
        backgroundColor: t.surface,
        border: `1px solid ${t.borderStrong}`,
        borderRadius: '12px',
        padding: '32px'
      }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '600',
          marginBottom: '8px',
          fontFamily: 'Poppins, sans-serif',
          color: t.text
        }}>
          Organization Settings
        </h1>
        <p style={{
          fontSize: '14px',
          color: t.textSecondary,
          marginBottom: '32px',
          fontFamily: 'Poppins, sans-serif'
        }}>
          Manage your organization profile and members
        </p>

        {/* Success Message */}
        {success && (
          <div style={{
            backgroundColor: t.successBg,
            border: `1px solid ${t.success}`,
            color: t.success,
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            fontFamily: 'Poppins, sans-serif'
          }}>
            {success}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: t.errorBg,
            border: `1px solid ${t.error}`,
            color: t.error,
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            fontFamily: 'Poppins, sans-serif'
          }}>
            {error}
          </div>
        )}

        {/* Avatar Upload Section */}
        <AvatarUploadSection
          avatarUrl={selectedOrganization.avatar_url}
          avatarPreview={avatarPreview}
          avatarFile={avatarFile}
          avatarError={avatarError}
          organizationName={selectedOrganization.name}
          onFileSelect={handleAvatarSelect}
          onRemove={handleRemoveAvatar}
          isSubmitting={isSubmitting}
        />

        {/* Basic Info Form */}
        <OrganizationBasicInfo
          name={name}
          setName={setName}
          handle={handle}
          setHandle={setHandle}
          email={email}
          setEmail={setEmail}
          website={website}
          setWebsite={setWebsite}
          location={location}
          setLocation={setLocation}
          bio={bio}
          setBio={setBio}
          handleAvailable={handleAvailable}
          checkingHandle={checkingHandle}
        />

        {/* Save Button */}
        <button
          onClick={handleSubmit}
          disabled={!name.trim() || !handle.trim() || (handle !== originalHandle && handleAvailable === false) || checkingHandle || isSubmitting || uploadingAvatar}
          style={{
            width: '100%',
            backgroundColor: t.buttonBg,
            color: t.buttonText,
            padding: '12px',
            borderRadius: '12px',
            border: 'none',
            cursor: (!name.trim() || !handle.trim() || (handle !== originalHandle && handleAvailable === false) || checkingHandle || isSubmitting || uploadingAvatar) ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            fontFamily: 'Poppins, sans-serif',
            opacity: (!name.trim() || !handle.trim() || (handle !== originalHandle && handleAvailable === false) || checkingHandle || isSubmitting || uploadingAvatar) ? 0.5 : 1,
            marginBottom: '32px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting && !uploadingAvatar && name.trim() && handle.trim()) {
              e.target.style.transform = 'translateY(-2px)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
          }}
        >
          {uploadingAvatar ? 'Uploading Image...' : isSubmitting ? 'Saving...' : 'Save Changes'}
        </button>

        {/* Seats Management Section */}
        {canManageSeats && (
          <div style={{
            paddingTop: '32px',
            paddingBottom: '32px',
            borderTop: '1px solid #e7e5e4',
            borderBottom: '1px solid #e7e5e4',
            marginBottom: '32px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  fontFamily: 'Poppins, sans-serif',
                  color: t.text,
                  marginBottom: '4px'
                }}>
                  Members
                </h3>
                <p style={{
                  fontSize: '13px',
                  color: t.textSecondary,
                  fontFamily: 'Poppins, sans-serif'
                }}>
                  {seats.length} member{seats.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => setShowAddMethod(!showAddMethod)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: `1px solid ${t.borderStrong}`,
                  backgroundColor: 'transparent',
                  color: t.text,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Poppins, sans-serif',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = t.accent;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = t.borderStrong;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <UserPlus size={16} />
                Add Member
              </button>
            </div>

            {/* Seats Table */}
            <SeatsTable
              seatsWithNames={seatsWithNames}
              invites={invites}
              loadingSeats={loadingSeats}
              userRole={userRole}
              onRoleChange={handleRoleChange}
              onRemoveSeat={handleRemoveSeat}
              onCopyInvite={copyInviteLink}
              onRevokeInvite={revokeInvite}
              copiedInvite={copiedInvite}
            />

            {/* Add Member Panel */}
            {showAddMethod && (
              <AddMemberPanel
                userRole={userRole}
                onAddByWallet={handleAddByWallet}
                onGenerateInvite={handleGenerateInvite}
                addingMember={addingMember}
              />
            )}
          </div>
        )}

        {/* Danger Zone - Delete */}
        {canDelete && (
          <div style={{
            borderTop: '1px solid #e7e5e4',
            paddingTop: '24px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '8px',
              fontFamily: 'Poppins, sans-serif',
              color: t.text
            }}>
              Danger Zone
            </h3>
            <p style={{
              fontSize: '13px',
              color: t.textSecondary,
              marginBottom: '16px',
              fontFamily: 'Poppins, sans-serif'
            }}>
              Once you delete an organization, there is no going back. Please be certain.
            </p>
            <button
              onClick={handleDelete}
              disabled={isSubmitting}
              style={{
                width: '100%',
                backgroundColor: 'transparent',
                color: t.error,
                padding: '12px',
                borderRadius: '6px',
                border: `1px solid ${t.error}`,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'Poppins, sans-serif',
                opacity: isSubmitting ? 0.5 : 1,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.target.style.backgroundColor = t.error;
                  e.target.style.color = t.surface;
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = t.error;
              }}
            >
              Delete Organization
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
